import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { PrescriptionHelper } from "@/Common/Helpers/PrescriptionHelper";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";

export class PrescriptionRenewalsMedicationHistoryItem {
    public medication: string;
    public beginning: Date;
    public startDatePrecision: LifeStageDatePrecisionEnum;
    public end: Date;
    public dosage: string[];
    public quantity: string[];
    public refills: number;
    public duration: number;
    public prescriber: Employee;
    public statusId: number;
    public status: string;
    public instruction: string;
    public origin: string;
    public isCeased: boolean;
    public ceasedReason: string;
    public ceaseDate: Date;
    public isLongTerm: boolean;
    public isExpiredLongTermMed: boolean;
    public externalPrescriberDisplayName: string;
    public writtenDate: Date;
    public writtenDatePrecision: LifeStageDatePrecisionEnum;

    constructor(fields?: Partial<PrescriptionRenewalsMedicationHistoryItem>) {
        Object.assign(this, fields);
    }

    /**
     * Converts MedicationModel to PrescriptionRenewalsMedicationHistoryItem
     * @param {MedicationModel} - medicationModel to convert into PrescriptionRenewalsMedicationHistoryItem
     * @returns {PrescriptionRenewalsMedicationHistoryItem} - converted PrescriptionRenewalsMedicationHistoryItem
     */
    public static convertMedicationModel(medication: MedicationModel, providedPrescriber: Employee = null): PrescriptionRenewalsMedicationHistoryItem {
        return new PrescriptionRenewalsMedicationHistoryItem({
            medication: medication.GetName(),
            beginning: medication.Model.RefillStartDate,
            startDatePrecision: medication.Model.StartDatePrecision,
            end: medication.EffectiveEndDate(),
            dosage: medication.GetDosagesDisplay({ includeRenewalsOnLastDosage: false }),
            quantity: medication.getMedicationQuantities(),
            refills: medication.Model.NumberOfRefill,
            duration: medication.Model.TotalDurationManualDays,
            prescriber: providedPrescriber ?? cacheStore.GetEmployeeByContactFacetId(medication.Model.EmployeeId),
            status: medication.GetStatusString(),
            statusId: medication.Model.SummaryDisplayStatus,
            instruction: medication.GetInstruction(),
            origin: medication.GetOriginString(),
            isCeased: medication.IsCeased(),
            ceasedReason: medication.getReasonWithLabel(),
            ceaseDate: medication.Model.CeaseDate,
            isLongTerm: medication.Model.IsLongTerm,
            isExpiredLongTermMed: medication.isExpiredLongTermMed(),
            externalPrescriberDisplayName: medication.Model.ExternalPrescriberDisplayName,
            writtenDate: medication.Model.WrittenDate,
            writtenDatePrecision: medication.Model.WrittenDatePrecision
        });
    }

    public static isSearchTextIncluded(medicationHistoryItem: PrescriptionRenewalsMedicationHistoryItem, searchText: string): boolean {
        const fixedFormat = "yyyy-MM-dd";
        const lowerCasedSearchText = searchText.toLowerCase();
        const prescriberName = cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields && PrescriptionHelper.IsPrescribedExternally(medicationHistoryItem.externalPrescriberDisplayName)
            ? medicationHistoryItem.externalPrescriberDisplayName?.toLowerCase()
            : Employee.getFullNameWithPrefixContactFacet(medicationHistoryItem.prescriber?.ContactFacet_id)?.toLowerCase() ?? "";

        let startDateLocalWithPrecision: string;
        if (cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields) {
            startDateLocalWithPrecision = PatientMedicalItemsHelper.GetLifeStageDateString(
                LuxonDateTimeParser.parse(medicationHistoryItem.beginning)?.toLocal()?.toJSDate(),
                medicationHistoryItem.startDatePrecision
            ) ?? "";
        }
        else {
            startDateLocalWithPrecision = LuxonDateTimeParser.parse(medicationHistoryItem.beginning)?.toLocal().toFormat(fixedFormat) ?? "";
        }

        return (
            medicationHistoryItem.medication.toLowerCase().contains(lowerCasedSearchText)
            || startDateLocalWithPrecision?.contains(lowerCasedSearchText)
            || LuxonDateTimeParser.parse(medicationHistoryItem.end)?.toLocal().toFormat(fixedFormat)?.contains(lowerCasedSearchText)
            || medicationHistoryItem.dosage?.join(" ").toLowerCase().contains(lowerCasedSearchText)
            || medicationHistoryItem.quantity?.join(" ").toLowerCase().contains(lowerCasedSearchText)
            || medicationHistoryItem.refills?.toString().contains(lowerCasedSearchText)
            || medicationHistoryItem.duration?.toString().contains(lowerCasedSearchText)
            || medicationHistoryItem.status?.toLowerCase().contains(lowerCasedSearchText)
            || (
                !cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                && Employee.getFullNameWithPrefixContactFacet(medicationHistoryItem.prescriber?.ContactFacet_id)?.toLowerCase().contains(lowerCasedSearchText)
            )
            || (
                cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                && (
                    prescriberName.contains(lowerCasedSearchText)
                    || LuxonDateTimeParser.parse(medicationHistoryItem.writtenDate)?.toLocal().toFormat(fixedFormat)?.contains(lowerCasedSearchText)
                )
            )
        );
    }
}
